<template>
    <div class="data-entry-notes-panel">
        <div class="data-entry-note-input loading-container">
            <NoteEdit :note="create" @cancel="resetCreate" @commit="addNote" />
        </div>

        <div class="data-entry-note-list loading-container">
            <loading :is-full-page="false" :active="loading" />
            <div class="data-entry-note-container" v-for="note in notes" :key="note.id">
                <NoteEdit
                    :note="editing"
                    @cancel="editing = null"
                    @commit="finishEdit"
                    v-if="editing === note"
                    autofocus
                />
                <NoteView :note="note" @edit="beginEdit" @destroy="beginDestroy" v-else />
            </div>
        </div>

        <ConfirmModal
            v-if="destroying"
            @dismiss="destroying = null"
            @commit="finishDestroy"
            title="Confirm Delete"
            message="Are you sure you want to delete this note?"
        />
    </div>
</template>
<script>
import DataEntryNotesService from '@/services/manage/dataEntryNotesService'
import NoteEdit from './NoteEdit'
import NoteView from './NoteView'
import ConfirmModal from '@/components/modals/ConfirmModal'

export default {
    components: {
        ConfirmModal,
        NoteEdit,
        NoteView,
    },
    props: {
        entityType: {
            type: String,
            default: null,
        },
        entityId: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            destroying: null,
            editing: null,
            create: {
                noteText: '',
                user: { firstName: '', lastName: '' },
            },
            saving: false,
        }
    },
    watch: {
        entityType() {
            this.editing = null
            this.deleting = null
            this.resetCreate()
        },
        entityId() {
            this.editing = null
            this.deleting = null
            this.resetCreate()
        },
    },
    computed: {
        loading() {
            return this.$asyncComputed.notes.updating
        },
    },
    asyncComputed: {
        notes: {
            async get() {
                const notes = await DataEntryNotesService.listByEntityTypeAndEntityId(this.entityType, this.entityId)
                notes.sort((lhs, rhs) => rhs.createdDate.localeCompare(lhs.createdDate))
                return notes
            },
            default: [],
        },
    },
    methods: {
        async addNote(create) {
            this.saving = true
            const note = {
                entityType: this.entityType,
                entityId: this.entityId,
                ...create,
            }

            const { ok, content } = await DataEntryNotesService.create(note)

            if (ok) {
                this.resetCreate()
                this.notes.push(content)
                this.notes.sort((lhs, rhs) => rhs.createdDate.localeCompare(lhs.createdDate))
                this.$flash('Note added', 'success')
            } else {
                this.$flash('Failed to add note', 'error')
            }
            this.saving = false
        },
        beginDestroy(note) {
            this.destroying = note
        },
        beginEdit(note) {
            this.editing = note
        },
        async finishDestroy() {
            this.saving = true
            const { ok } = await DataEntryNotesService.destroy(this.destroying)

            if (ok) {
                this.notes.splice(this.notes.indexOf(this.destroying), 1)
                this.destroying = null
                this.$flash('Note deleted', 'success')
            } else {
                this.$flash('Failed to delete note', 'error')
            }
            this.saving = false
        },
        async finishEdit(updated) {
            this.saving = true
            const { ok, status, content } = await DataEntryNotesService.update(updated)

            if (ok) {
                this.notes.splice(this.notes.indexOf(this.editing), 1, content)
                this.editing = null
                this.$flash('Note updated', 'success')
            } else if (status === 403) {
                this.$flash('You do not have permission to update this note', 'error')
            } else {
                this.$flash('Failed to update note', 'error')
            }
            this.saving = false
        },
        resetCreate() {
            this.create = { ...this.create, noteText: '' }
        },
    },
}
</script>
<style>
.data-entry-notes-panel {
    height: 100%;
    overflow: auto;
    position: relative;

    .data-entry-note-input {
        background-color: white;
        position: sticky;
        top: 0;
        z-index: calc(var(--z-index-loading-container) + 1);
    }

    .data-entry-note-list {
        margin-top: 24px;
        padding-bottom: 24px;
    }

    .data-entry-note-container {
        margin-top: 24px;
    }
}
</style>
